import { formatAppTime } from "./appTimeFormat";

export const TAB_FOLLOW = "follow";
export const TAB_UNFOLLOW = "unfollow";

export const formatFollowerCount = (n) => {
  const count = Number(n) || 0;
  return `${count.toLocaleString()} follower${count === 1 ? "" : "s"}`;
};

export const followerEventDateKey = (row) => {
  const when = row?.occurredAt || row?.followedAt;
  if (!when) return "unknown";
  const d = new Date(when);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const formatEventTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return formatAppTime(d);
};

export const formatPeakHour = (hour) => {
  if (hour == null || Number.isNaN(Number(hour))) return "—";
  return `${String(hour).padStart(2, "0")}:00`;
};

export const isUnfollowAction = (row) =>
  String(row?.action || "follow").toLowerCase() === TAB_UNFOLLOW;

export const filterFollowerRows = (rows, activeTab) =>
  (Array.isArray(rows) ? rows : []).filter((row) => {
    const unfollow = isUnfollowAction(row);
    return activeTab === TAB_UNFOLLOW ? unfollow : !unfollow;
  });

export const normalizeFollowerRow = (row) => ({
  userId: row.userId,
  username: row.username || "Follower",
  action: isUnfollowAction(row) ? TAB_UNFOLLOW : TAB_FOLLOW,
  occurredAt: row.occurredAt || row.followedAt || null,
});

export const normalizeFollowerList = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map(normalizeFollowerRow);

/** Pulse from GET /api/bet/:houseId/followers — aggregated from bet.followerEvents in MongoDB. */
export const normalizeFollowerPulse = (pulse) => {
  if (!pulse || typeof pulse !== "object") {
    return { loaded: false };
  }

  return {
    loaded: true,
    totalEvents: Number(pulse.totalEvents) || 0,
    totalFollows: Number(pulse.totalFollows) || 0,
    totalUnfollows: Number(pulse.totalUnfollows) || 0,
    currentFollowers: Number(pulse.currentFollowers) || 0,
    latestEventAt: pulse.latestEventAt ?? null,
    latestFollowedAt: pulse.latestFollowedAt ?? null,
    latestUnfollowedAt: pulse.latestUnfollowedAt ?? null,
    followedLast24h: Number(pulse.followedLast24h) || 0,
    unfollowedLast24h: Number(pulse.unfollowedLast24h) || 0,
    peakHour: pulse.peakHour ?? null,
    peakHourCount: Number(pulse.peakHourCount) || 0,
    followPeakHour: pulse.followPeakHour ?? null,
    followPeakHourCount: Number(pulse.followPeakHourCount) || 0,
    unfollowPeakHour: pulse.unfollowPeakHour ?? null,
    unfollowPeakHourCount: Number(pulse.unfollowPeakHourCount) || 0,
    followByHour: Array.isArray(pulse.followByHour)
      ? pulse.followByHour.map((n) => Number(n) || 0)
      : null,
    unfollowByHour: Array.isArray(pulse.unfollowByHour)
      ? pulse.unfollowByHour.map((n) => Number(n) || 0)
      : null,
    dailyTrend: Array.isArray(pulse.dailyTrend) ? pulse.dailyTrend : null,
    topFollowers: Array.isArray(pulse.topFollowers) ? pulse.topFollowers : null,
  };
};

export const sumSeries = (values = []) =>
  (values || []).reduce((sum, value) => sum + (Number(value) || 0), 0);

export const maxSeriesValue = (values) =>
  Math.max(0, ...(values || []).map((v) => Number(v) || 0));

/**
 * Cumulative net growth series from the 7-day trend:
 * per-day net (follows − unfollows) plus a running total.
 */
export const buildNetGrowthSeries = (dailyTrend) => {
  if (!Array.isArray(dailyTrend)) return [];
  let cumulative = 0;
  return dailyTrend.map((day) => {
    const follows = Number(day?.follows) || 0;
    const unfollows = Number(day?.unfollows) || 0;
    const net = follows - unfollows;
    cumulative += net;
    return {
      date: day?.date ?? day?.label ?? "",
      label: day?.label ?? "",
      follows,
      unfollows,
      net,
      cumulative,
    };
  });
};

/**
 * Churn ratios: unfollows-to-follows for the 7-day window and lifetime.
 * Ratio of 0.3 reads as "for every 10 follows you lose 3".
 */
export const computeChurnStats = (pulse, netSeries = []) => {
  let windowFollows = 0;
  let windowUnfollows = 0;
  for (const day of netSeries) {
    windowFollows += day.follows;
    windowUnfollows += day.unfollows;
  }
  const lifetimeFollows = Number(pulse?.totalFollows) || 0;
  const lifetimeUnfollows = Number(pulse?.totalUnfollows) || 0;

  const ratio = (unf, fol) => (fol > 0 ? unf / fol : unf > 0 ? 1 : null);

  const windowChurn = ratio(windowUnfollows, windowFollows);
  const lifetimeChurn = ratio(lifetimeUnfollows, lifetimeFollows);

  const toneOf = (r) => (r == null ? "flat" : r <= 0.2 ? "low" : r <= 0.45 ? "mid" : "high");

  return {
    windowFollows,
    windowUnfollows,
    windowChurn,
    windowTone: toneOf(windowChurn),
    lifetimeFollows,
    lifetimeUnfollows,
    lifetimeChurn,
    lifetimeTone: toneOf(lifetimeChurn),
    lostPerTen: windowChurn != null ? Math.round(windowChurn * 10) : null,
  };
};
